import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "../game/game.css";
import "./gameContent.css";
import { fetchThemeData } from "../../admin/themeSlice";
import { shouldShowIntroVideo } from "../../functions/drReddyOrg";
import { getDvSourceImageUrl, toCssUrlValue } from "../../functions/themeAssets";

// Same fallback as GamePage's .mf-root — kept in sync so this page's ambient
// background matches when the theme has no uploaded background image.
const AMBIENT_GRADIENT_FALLBACK = [
  "radial-gradient(950px 660px at 10% -8%,  rgba(199,87,76,.26),  transparent 64%)",
  "radial-gradient(950px 660px at 92% 110%, rgba(46,139,103,.28), transparent 64%)",
  "radial-gradient(1150px 680px at 50% 34%, rgba(217,166,46,.20), transparent 66%)",
  "radial-gradient(700px 500px at 96% -4%,  rgba(46,139,103,.12), transparent 60%)",
  "radial-gradient(700px 500px at 4% 106%,  rgba(199,87,76,.12),  transparent 60%)",
].join(", ");

/**
 * Post-login "game content" screen (see mythfact_old/game.php for the legacy
 * equivalent) — admin's `game_content` bullets shown pointer-wise before the
 * player is dropped into the actual quiz.
 */
export default function GameContent() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: themeData } = useSelector((state) => state.theme);
  const authUser = useSelector((state) => state.auth.user);
  const sessionSlice = useSelector((state) => state.session);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  const [isMobileViewport, setIsMobileViewport] = useState(
    () => window.matchMedia("(max-width: 640px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const onChange = () => setIsMobileViewport(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const backgroundImageUrl = useMemo(() => {
    if (!themeData) return "";
    const key = isMobileViewport ? themeData.background_mob : themeData.background_desk;
    return key ? getDvSourceImageUrl(key) : "";
  }, [themeData, isMobileViewport]);

  // Same page-wide background technique as GamePage.jsx — applied to html/body
  // so it spans behind the app's outer header too, pinned to the viewport.
  useEffect(() => {
    const targets = [document.documentElement, document.body];
    const image = backgroundImageUrl ? toCssUrlValue(backgroundImageUrl) : AMBIENT_GRADIENT_FALLBACK;
    for (const el of targets) {
      el.style.backgroundImage = image;
      el.style.backgroundSize = "cover";
      el.style.backgroundPosition = "top center";
      el.style.backgroundRepeat = "no-repeat";
      el.style.backgroundAttachment = "fixed";
    }
    return () => {
      for (const el of targets) {
        el.style.removeProperty("background-image");
        el.style.removeProperty("background-size");
        el.style.removeProperty("background-position");
        el.style.removeProperty("background-repeat");
        el.style.removeProperty("background-attachment");
      }
    };
  }, [backgroundImageUrl]);

  const themeVars = useMemo(
    () => ({
      "--theme-accent": themeData?.button_color || undefined,
      "--theme-accent-tint": themeData?.icon_bg_color || undefined,
      "--theme-text": themeData?.textcolor || undefined,
      "--theme-button-text": themeData?.button_Textcolor || undefined,
      "--theme-card-bg": themeData?.text_bg_color || undefined,
    }),
    [themeData]
  );

  const textColor = useMemo(() => {
    const raw = themeData?.landing_page_title_color ?? themeData?.textcolor;
    if (raw == null || raw === "") return undefined;
    const c = String(raw).trim();
    return c || undefined;
  }, [themeData]);

  const gameContent = useMemo(() => {
    const raw = themeData?.game_content;
    if (Array.isArray(raw)) return raw.filter((item) => String(item ?? "").trim() !== "");
    return [];
  }, [themeData]);

  const handleContinue = () => {
    if (shouldShowIntroVideo({ themeData, authUser, sessionSlice })) {
      navigate("/videointro");
      return;
    }
    navigate("/game");
  };

  if (!themeData) {
    return (
      <div className="mf-loading">
        <p>Loading...</p>
      </div>
    );
  }

  const logoUrl = themeData?.logo ? getDvSourceImageUrl(themeData.logo) : "";

  return (
    <div className="mf-root gc-root" style={themeVars}>
      <div className="gc-app">
        {logoUrl ? (
          <div className="gc-logo-row">
            <img src={logoUrl} alt="Logo" className="gc-logo" />
          </div>
        ) : null}

        <div className="gc-stage">
          <p className="mf-q-eyebrow gc-eyebrow">Before You Begin</p>
          <div style={textColor ? { color: textColor } : undefined}>
            {gameContent.length > 0 ? (
              <ul className="gc-list">
                {gameContent.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            ) : (
              <p className="gc-empty">Get ready to play!</p>
            )}
          </div>

          <div className="gc-cta">
            <button type="button" className="mf-btn mf-btn-primary" onClick={handleContinue}>
              Start!
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
